import type * as Highcharts from 'highcharts';
import { number_format } from '../util';
import { makeEmptyAwareTooltip } from '../utils/emptyAwareTooltip';

/**
 * tooltips.ts (Phase 279 D-10) — the ONE per-key map every /home chart's tooltip formatter
 * lives in, replacing the 176-line `id === '...' ? ... : id === '...' ? ...` ternary chain that
 * used to sit inline in home.ts's `renderChart()`.
 *
 * This module is the enumeration surface the D-09 probe (`tools/probe-279-tooltip-keys.php`)
 * statically scans: every `HomeModel::GRAPH_KEYS` key MUST have an entry here that calls
 * `makeEmptyAwareTooltip`. Adding a chart without an entry here is a probe failure to fix in
 * this file — never an allowlist entry added to the probe. The probe's `wireNullAwareHover`
 * half still targets `src/js/home.ts` (that half of the null-aware-hover contract was not
 * moved) — see 279-03-PLAN.md's Design Note for the split.
 *
 * `buildTooltipFor(id, data, yoyExtraRows)` is a FACTORY, not a flat object literal, because the
 * ported branches close over `data` (`data.suffix`, `data.total_data`), `id` (shows_movies vs
 * music's h/m suffix) and `yoyExtraRows` (alc/weight/waist's Current/Prior/Change rows) — a bare
 * `Record` would lose that closure. The default branch is `makeEmptyAwareTooltip()`, never
 * `undefined`: "no entry" degrades to the empty-aware default, not to Highcharts' native
 * formatter, so D-12's zero-exemptions rule holds even if a 21st key is registered later.
 */

// Ported verbatim from src/js/home.ts (private module-scope helper, not exported from ../util) —
// duplicated here rather than importing from home.ts, which this plan does not edit (279-04 wires
// the map into renderChart()).
function diffForHumans(milliseconds: number, parts = 2): string {
    let totalSeconds = Math.floor(milliseconds / 1000);
    let components = [];

    let days = Math.floor(totalSeconds / 86400);
    let hours = Math.floor((totalSeconds % 86400) / 3600);
    let minutes = Math.floor((totalSeconds % 3600) / 60);
    let seconds = totalSeconds % 60;

    if (days > 0) components.push(`${days}d`);
    if (hours > 0) components.push(`${hours}h`);
    if (minutes > 0) components.push(`${minutes}m`);
    if (seconds > 0) components.push(`${seconds}s`);

    if (components.length > parts) {
        components = components.slice(0, parts);
    }

    return components.join(' ');
}

// Ported verbatim from src/js/home.ts (private module-scope helpers) — same duplication rationale
// as diffForHumans above.
const LBS_TO_KG = 0.45359237;
const CM_TO_IN = 1 / 2.54;

function fmtDualUnit(primaryVal: number, suffix: string, chartId: string): string {
    if (chartId === 'weight') {
        return `${primaryVal}${suffix} (${(primaryVal * LBS_TO_KG).toFixed(1)} kg)`;
    }
    if (chartId === 'waist') {
        return `${primaryVal}${suffix} (${(primaryVal * CM_TO_IN).toFixed(1)} in)`;
    }
    return `${primaryVal}${suffix}`;
}

export function buildTooltipFor(
    id: string,
    data: any,
    yoyExtraRows?: (ctx: Highcharts.Point) => string[]
): Highcharts.TooltipFormatterCallbackFunction {
    const map: Record<string, () => Highcharts.TooltipFormatterCallbackFunction> = {
        'reading': () => makeEmptyAwareTooltip({
            formatValue: (ctx) => diffForHumans(ctx.y as number, 2),
        }),
        'km': () => makeEmptyAwareTooltip({
            formatValue: (ctx) => {
                const breakdown = ((ctx as any).point as any).breakdown;
                const hasOura = ((ctx as any).point as any).hasOura === true;
                const ouraLabel = hasOura
                    ? ' <span style="color:var(--ksat-accent)">&#9899; Oura</span>'
                    : '';
                return `${ctx.y} km${breakdown ? ` (${breakdown})` : ''}${ouraLabel}`;
            },
        }),
        'stairmaster': () => makeEmptyAwareTooltip({
            formatValue: (ctx) => {
                const breakdown = ((ctx as any).point as any).breakdown;
                return `${ctx.y} steps${breakdown ? ` (${breakdown})` : ''}`;
            },
        }),
        'alc': () => makeEmptyAwareTooltip({
            formatValue: (ctx) => {
                const value = Math.abs(ctx.y as number);
                const isEstimate = ((ctx as any).point as any).custom?.estimate === 1;
                const estLabel = isEstimate
                    ? ' <span style="color:#999;font-style:italic;">(est.)</span>'
                    : '';
                return `${value}${estLabel}`;
            },
            extraRows: yoyExtraRows,
        }),
        'coffee': () => makeEmptyAwareTooltip({
            formatValue: (ctx) => {
                const value = ctx.y as number;
                const isEstimate = ((ctx as any).point as any).custom?.estimate === 1;
                const estLabel = isEstimate
                    ? ' <span style="color:#999;font-style:italic;">(est.)</span>'
                    : '';
                return `${value}${estLabel}`;
            },
        }),
        'transactions': () => makeEmptyAwareTooltip({ shared: true }),
        'weight': () => makeEmptyAwareTooltip({
            formatValue: (ctx) => fmtDualUnit(ctx.y as number, (data.suffix ?? '') as string, id.toString()),
            extraRows: yoyExtraRows,
        }),
        'waist': () => makeEmptyAwareTooltip({
            formatValue: (ctx) => fmtDualUnit(ctx.y as number, (data.suffix ?? '') as string, id.toString()),
            extraRows: yoyExtraRows,
        }),
        'gym': () => makeEmptyAwareTooltip({
            // Multi-series stacked column — same layout as
            // transactions (date + Total + sorted-desc per
            // muscle), but counts not currency.
            shared: true,
            sharedValueFormatter: (y) => String(y),
        }),
        'weed': () => makeEmptyAwareTooltip({
            // D-02/D-11: grouped puffs + THC-drinks share one tooltip; the
            // single row-level estimate flag appends "(est.)" per series row.
            shared: true,
            // D-03: no combined total. Unlike gym's shared arm (where every
            // series is the same unit, so summing is meaningful), puffs and
            // THC-drinks are different units — a "Total: 5" row mixing 3 puffs
            // with 2 drinks is a nonsense number, and with YoY on it would fold
            // in last year's series too. Suppress the total row; the per-series
            // rows below carry each metric on its own.
            sharedTotalLabel: false,
            sharedValueFormatter: (y, point) => {
                const isEstimate = (point as any)?.custom?.estimate === 1;
                const estLabel = isEstimate
                    ? ' <span style="color:#999;font-style:italic;">(est.)</span>'
                    : '';
                return `${y}${estLabel}`;
            },
        }),
        // D-11: converted from a bespoke `this.points`-reading function (which suppressed the
        // tooltip entirely on an empty date) to makeEmptyAwareTooltip's sharedRows hook — the
        // header + "No data" branch is now the helper's, the row body below is byte-identical to
        // the pre-refactor bespoke body (same toLocaleString calls, ● markers, <b> wrapping,
        // <br/> placement, series order, zero-valued rows kept — see tests/home/tooltip-parity.test.ts).
        'snap': () => makeEmptyAwareTooltip({
            shared: true,
            sharedRows: (points) => {
                let body = '';
                for (const p of points as any[]) {
                    if (p.y == null) continue;
                    const isEst = p.custom?.estimate === 1;
                    const estLabel = isEst
                        ? ' <span style="color:#999;font-style:italic;">(est.)</span>'
                        : '';
                    const dot = typeof p.series.color === 'string' ? p.series.color : '#aaa';
                    body += `<span style="color:${dot}">●</span> ${p.series.name}: `
                        + `<b>${(p.y as number).toLocaleString('en-US')}${estLabel}</b><br/>`;
                }
                return body;
            },
        }),
        // D-11: converted from a bespoke function to sharedRows, same rationale as snap above.
        // The pre-existing `pts.every(p => p.y == null)` empty-suppression guard is REMOVED here,
        // not carried into sharedRows — it is exactly the helper's own `allEmpty` check (both test
        // "every point's y is null"), so the helper's "No data" branch already covers this case
        // before sharedRows is ever called; keeping a second copy would be dead code.
        'cindy': () => makeEmptyAwareTooltip({
            shared: true,
            sharedRows: (points) => {
                const pts = points as any[];
                const custom = pts[0]?.custom;
                const scoreLine = custom ? `Score: ${custom.score}<br/>` : '';
                let body = '';
                let total = 0;
                for (const p of pts) {
                    if (p.y == null) continue;
                    total += p.y as number;
                    const dot = typeof p.series.color === 'string' ? p.series.color : '#aaa';
                    body += `<span style="color:${dot}">●</span> ${p.series.name}: `
                        + `<b>${(p.y as number).toLocaleString('en-US')}</b><br/>`;
                }
                return scoreLine + body + `Total: ${total.toLocaleString('en-US')} reps`;
            },
        }),
        'mood': () => makeEmptyAwareTooltip(),
        'bmi': () => makeEmptyAwareTooltip(),
        'shows_movies': () => makeEmptyAwareTooltip({
            // D-08/GRAPH-03/04: reuse gym's multi-series shared shape — date
            // header + Total + sorted-desc per title/artist — swapping counts
            // for the graph's own unit (hours for shows/movies, minutes for music).
            shared: true,
            // shows_movies values are round($minutes/60, 2) floats, and the shared
            // Total row sums them — interpolating the raw IEEE-754 sum would
            // render e.g. "1.0499999999999998h". music is integer minutes.
            sharedValueFormatter: (y) => id === 'shows_movies'
                ? `${Math.round(y * 100) / 100}h`
                : `${y}m`,
        }),
        'music': () => makeEmptyAwareTooltip({
            shared: true,
            sharedValueFormatter: (y) => id === 'shows_movies'
                ? `${Math.round(y * 100) / 100}h`
                : `${y}m`,
        }),
        // D-11: converted from a bespoke function to sharedRows, same rationale as snap/cindy
        // above. Both pre-existing guards (`pts.length === 0` and `stagePts.length === 0 &&
        // !scorePt`) are REMOVED here, not carried into sharedRows — together stagePts and
        // scorePt partition every point with a non-null y, so both guards are exactly the
        // helper's own `allEmpty` check restated, and can never fire once sharedRows is reached
        // (allEmpty already handled the "every point's y is null" case first). The "stages
        // present but score missing" mixed case is NOT suppressed: whatever rows exist (stage
        // rows, the Total row, the Sleep Score row) render independently below, same as before.
        'oura_sleep': () => makeEmptyAwareTooltip({
            shared: true,
            sharedRows: (points) => {
                const pts = points as any[];
                const fmtHM = (secs: number) => {
                    const h = Math.floor(secs / 3600);
                    const m = Math.floor((secs % 3600) / 60);
                    return `${h}h ${String(m).padStart(2, '0')}m`;
                };
                const stagePts = pts.filter(p => p.series.name !== 'Sleep Score' && p.y != null);
                const scorePt = pts.find(p => p.series.name === 'Sleep Score' && p.y != null);
                let body = '';
                for (const p of stagePts) {
                    body += `<span style="color:${p.series.color}">●</span> ${p.series.name}: `
                        + `<b>${fmtHM(p.y as number)}</b><br/>`;
                }
                const idxSrc = stagePts[0] ?? scorePt;
                const idx = idxSrc ? (idxSrc as any).index : undefined;
                const total = idx != null ? (data.total_data ?? [])[idx] : undefined;
                if (total != null) {
                    body += `<span style="color:#999">Total:</span> <b>${fmtHM(total)}</b><br/>`;
                }
                if (scorePt) {
                    body += `<span style="color:${scorePt.series.color}">●</span> Sleep Score: <b>${scorePt.y}</b><br/>`;
                }
                return body;
            },
        }),
        // Phase 279 D-12: previously had no formatter at all (fell through to `undefined` —
        // Highcharts' native tooltip). Single column series, integer step counts.
        'oura_steps': () => makeEmptyAwareTooltip({
            formatValue: (ctx) => number_format(ctx.y as number, 0),
        }),
        // Phase 279 D-12: previously had no formatter at all. Single column series, a signed
        // profit value with decimals.
        'trades_gold': () => makeEmptyAwareTooltip({
            formatValue: (ctx) => number_format(ctx.y as number, 2),
        }),
        // Phase 279 D-12: previously had no formatter at all. Three series — Sell Orders/Buy
        // Orders counts on the primary axis, Gold as a spline on the secondary axis. Shared mode
        // with the total row suppressed (summing order counts with a gold value is meaningless,
        // same reasoning as weed's sharedTotalLabel: false above).
        'trades': () => makeEmptyAwareTooltip({
            shared: true,
            sharedTotalLabel: false,
            sharedValueFormatter: (y, point) => point?.series.name === 'Gold'
                ? number_format(y, 2)
                : number_format(y, 0),
        }),
    };

    return map[id]?.() ?? makeEmptyAwareTooltip();
}
